/**
 * Linda Chat Service — Main Orchestration
 *
 * Replaces the deleted Next.js /api/linda/chat route handler.
 * Sends messages to the LiteLLM gateway, executes lookup tools
 * client-side, feeds results back to Claude in a loop, and
 * transforms the response for the existing chat panel UI.
 */

import {logger} from '@/lib/logger';
import {
    chatCompletion,
    LITELLM_MODELS,
    type ChatMessage,
    type ToolCall,
} from '@/lib/litellm-client';
import {LINDA_TOOLS, isLookupTool, isRenderRequest} from '@/lib/linda-tools';
import {executeLookupTool} from '@/lib/linda-tool-handlers';
import {buildSystemPrompt} from '@/lib/linda-system-prompt';
import type {ChatContext} from '@/store/linda-chat';
import type {
    LindaAction,
    SearchResults,
} from '@/components/linda-chat-panel/types';

const log = logger.tag('LindaChatService');

/** Max tool loop iterations to prevent infinite loops */
const MAX_TOOL_ITERATIONS = 5;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

interface NotificationData {
    type: string;
    status: string;
    title: string;
    productName?: string;
}

interface SendLindaChatParams {
    message: string;
    conversationHistory: Array<{role: string; content: string}>;
    context: ChatContext;
    notifications: NotificationData[];
    tokens: {modelbank_token: string; auth_token: string};
    selectedSupplierId?: string;
}

interface SendLindaChatResult {
    message: string;
    actions?: LindaAction[];
    searchResults?: SearchResults;
    notification?: {
        type: 'video' | 'silo' | '360-spin';
        status: 'processing' | 'completed' | 'failed' | 'cancelled';
        title: string;
        message: string;
        productId?: string;
        productName?: string;
        requestId?: string;
        assetType?: 'product' | 'group';
    };
}

export async function sendLindaChat(
    params: SendLindaChatParams
): Promise<SendLindaChatResult> {
    const {
        message,
        conversationHistory,
        context,
        notifications,
        tokens,
        selectedSupplierId,
    } = params;

    // 1. Build system prompt
    const systemPrompt = buildSystemPrompt(
        context,
        context.userName,
        notifications
    );

    // 2. Assemble messages
    // conversationHistory already includes the current user message
    // (appended by the caller), so we do NOT append it again.
    const messages: ChatMessage[] = [
        {role: 'system', content: systemPrompt},
        ...conversationHistory.slice(-20).map(
            (msg): ChatMessage => ({
                role: msg.role as 'user' | 'assistant',
                content: msg.content,
            })
        ),
    ];

    // 3. Detect render requests to force tool use (matches original behavior)
    const isRenderReq = isRenderRequest(message.toLowerCase());

    // 4. Tool loop
    let allActions: LindaAction[] = [];
    let lastSearchResults: SearchResults | undefined;
    let lastNotification: SendLindaChatResult['notification'];
    let lastContent: string | null = null;

    for (let iteration = 0; iteration < MAX_TOOL_ITERATIONS; iteration++) {
        log.debug('Chat completion request', {
            iteration,
            messageCount: messages.length,
        });

        const response = await chatCompletion({
            model: LITELLM_MODELS.sonnet,
            messages,
            tools: LINDA_TOOLS,
            // Force tool use on first iteration for render requests
            tool_choice: isRenderReq && iteration === 0 ? 'required' : 'auto',
            temperature: 0.7,
            max_tokens: 1024,
            metadata: {
                user_id: context.userId,
                iteration,
            },
        });

        const choice = response.choices[0];
        if (!choice) {
            throw new Error('No response from Claude');
        }

        lastContent = choice.message.content;
        const toolCalls = choice.message.tool_calls ?? [];

        if (toolCalls.length === 0) {
            // No tool calls — we're done
            break;
        }

        // Separate lookup tools from action tools
        const lookupCalls = toolCalls.filter((tc) =>
            isLookupTool(tc.function.name)
        );
        const actionCalls = toolCalls.filter(
            (tc) => !isLookupTool(tc.function.name)
        );

        // Collect action tools as LindaAction[]
        for (const tc of actionCalls) {
            allActions = [...allActions, ...parseActionToolCalls([tc])];
        }

        // If no lookup tools, we're done
        if (lookupCalls.length === 0) {
            break;
        }

        // Execute lookup tools and build tool_result messages
        // First, add the assistant's response with tool calls
        messages.push({
            role: 'assistant',
            content: choice.message.content,
            tool_calls: toolCalls,
        });

        // Then add tool results for each lookup call
        for (const tc of lookupCalls) {
            const args = safeParseArgs(tc);
            const result = await executeLookupTool(
                tc.function.name,
                args,
                tokens,
                selectedSupplierId
            );

            // Collect structured results for UI
            if (result.searchResults) {
                lastSearchResults = result.searchResults;
            }
            if (result.notification) {
                lastNotification = result.notification;
            }

            // Send JSON to Claude (matches original behavior — Claude
            // receives structured data it can reference precisely)
            messages.push({
                role: 'tool',
                content: result.toolResultJson,
                tool_call_id: tc.id,
            });
        }

        // Also add empty tool results for action tools so the API doesn't complain
        for (const tc of actionCalls) {
            messages.push({
                role: 'tool',
                content: 'Action queued for client-side execution.',
                tool_call_id: tc.id,
            });
        }

        // Continue loop — Claude will process tool results
        log.debug('Lookup tools executed, continuing loop', {
            iteration,
            lookupCount: lookupCalls.length,
            actionCount: actionCalls.length,
        });
    }

    // 5. Build final response
    const finalMessage = lastContent ?? buildFallbackMessage(allActions);

    return {
        message: finalMessage,
        actions: allActions.length > 0 ? allActions : undefined,
        searchResults: lastSearchResults,
        notification: lastNotification,
    };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Parse action tool calls into LindaAction objects for the UI.
 */
function parseActionToolCalls(toolCalls: ToolCall[]): LindaAction[] {
    return toolCalls.map((tc) => ({
        type: tc.function.name,
        id: tc.id,
        input: safeParseArgs(tc),
    }));
}

/**
 * Safely parse tool call arguments JSON.
 */
function safeParseArgs(tc: ToolCall): Record<string, unknown> {
    try {
        return JSON.parse(tc.function.arguments) as Record<string, unknown>;
    } catch {
        log.warn('Failed to parse tool arguments', {
            tool: tc.function.name,
            args: tc.function.arguments,
        });
        return {};
    }
}

/**
 * Generate a fallback message when Claude returns tool calls but no text.
 */
function buildFallbackMessage(actions: LindaAction[]): string {
    if (actions.length === 0) {
        return 'Let me help you with that.';
    }

    const first = actions[0];
    const input = first.input;

    switch (first.type) {
        case 'navigate':
            return `Taking you to ${String(input.destination ?? 'that page')}...`;
        case 'search_products':
        case 'search_groups':
        case 'search_styles':
        case 'search_materials':
            return `Searching for "${String(input.query ?? '')}"...`;
        case 'filter_products':
        case 'filter_styles':
        case 'filter_groups':
        case 'filter_materials':
            return 'Applying those filters for you...';
        case 'select_product':
        case 'select_group':
        case 'select_style':
        case 'select_material':
            return 'Opening that up for you...';
        default:
            return 'Working on it...';
    }
}
